import React, { useState } from 'react';

import { useDispatch } from 'react-redux';
import { setSnackbar, SnackbarStates } from '../../redux/snackbar';
import { Button, Card, CardActions, CardContent, CardHeader, FormControl, Grid, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { setLoadingStatus } from '../../redux/loading';
import { requestPasswordChange } from '../../utils/passwordChange';

interface RequestPasswordChangeProps {
  goToLogin: () => void;
}

const EMAIL_VALIDATION_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function RequestPasswordChange({ goToLogin }: RequestPasswordChangeProps) {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [email, setEmail] = useState('');
  const [requestSend, setRequestSend] = useState(false);

  const resetRequestHandler = async (e: React.MouseEvent) => {
    e.preventDefault();
    dispatch(setLoadingStatus(true));

    if (email === undefined || email.length === 0) {
      dispatch(setSnackbar(true, SnackbarStates.ERROR, t('userError.notFilledEmail')));
      return false;
    }

    if (!EMAIL_VALIDATION_REGEX.test(email)) {
      dispatch(setSnackbar(true, SnackbarStates.ERROR, t('userError.invalidEmail')));
      return false;
    }

    try {
      await requestPasswordChange(email, 'FORGOTTEN_PASSWORD');
    } catch (error: unknown) {
      let reason = '';
      if (error instanceof Error) {
        reason = error.message;
      }
      dispatch(setSnackbar(true, SnackbarStates.ERROR, t('userError.genericRequestFailed', { reason })));
      return;
    } finally {
      dispatch(setLoadingStatus(false));
    }

    setRequestSend(true);
  };

  const resetStateAndGoBack = () => {
    setEmail('');
    setRequestSend(false);
    goToLogin();
  };

  return (
    <Card className='auth'>
      <CardHeader title={t('pages.loginPage.forgottenPassword.formTitle')} />
      <CardContent>
        <Grid
          container
          direction='row'
          justifyContent={'center'}
        >
          <Grid item>
            <FormControl error={email.length > 0 && !EMAIL_VALIDATION_REGEX.test(email)}>
              <TextField
                disabled={requestSend}
                error={email.length > 0 && !EMAIL_VALIDATION_REGEX.test(email)}
                label={t('fieldName.email')}
                required={true}
                placeholder={t('fieldName.emailPlaceholder')}
                inputMode='email'
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </FormControl>
          </Grid>
        </Grid>
      </CardContent>
      <CardActions>
        <Grid
          container
          direction='column'
          justifyContent={'flex-start'}
        >
          <Grid item>
            {!requestSend ? (
              <Button
                type='submit'
                className='btn btn-login'
                onClick={resetRequestHandler}
              >
                {t('actions.resetPassword')}
              </Button>
            ) : (
              <p>{t('pages.loginPage.forgottenPassword.requestSentMessage')}</p>
            )}
          </Grid>
          <Grid item>
            <Button
              variant='text'
              className='btn btn-forgotten-password'
              onClick={resetStateAndGoBack}
            >
              {t('actions.backToLogin')}
            </Button>
          </Grid>
        </Grid>
      </CardActions>
    </Card>
  );
}
